import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Archive, AlertTriangle } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { roleRoutes } from "../../utils/roles";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { formatPhone } from "../../layouts/helpdeskLayout/HelpdeskRail";
import { resolveOpenOfRetiredAccount } from "../../services/aiChatService";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import RetireNumberModal from "../../views/adminView/helpdesk/components/RetireNumberModal";

// Tope de vueltas del cierre en bloque: el backend cierra de a 100 por llamada;
// 50 vueltas son 5.000 hilos. Si quedara más, el aviso lo dice y se repite.
const MAX_PURGE_ROUNDS = 50;

/**
 * Aviso de la vista de un número RETIRADO sin reemplazo: explica por qué sus
 * conversaciones no están en las colas y ofrece las dos salidas — migrar el
 * número a otro (todo pasa al nuevo) o cerrar en bloque las que quedaron
 * abiertas (cierre general: no borra nada, quedan en "Resueltos").
 *
 * Ambas acciones son de supervisión (el backend exige supervisor o
 * administrador); a un agente se le muestra solo la explicación.
 *
 * @param {{account: object, openCount: number, onChanged: () => void}} props
 */
export default function RetiredAccountBanner({ account, openCount, onChanged }) {
  const { t } = useTranslation();
  // Sin AuthProvider (tests, montajes aislados) no hay usuario: sin permisos.
  const user = useAuth()?.user;
  const canManage = [
    roleRoutes.supervisor,
    roleRoutes.admin,
    roleRoutes.superadmin,
    roleRoutes.systemSuperadmin,
  ].includes(user?.role);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState(null);
  const [purgeSummary, setPurgeSummary] = useState(null);

  const phone = formatPhone(account?.phone) || account?.label || "";
  // Lo que el backend sabe (todas las abiertas) manda sobre lo cargado.
  const open = typeof account?.open_count === "number" ? account.open_count : openCount;

  const handlePurge = async () => {
    setPurging(true);
    setPurgeError(null);
    let resolved = 0;
    let failed = 0;
    let remaining = open;
    try {
      for (let round = 0; round < MAX_PURGE_ROUNDS && remaining > 0; round += 1) {
        const res = await resolveOpenOfRetiredAccount(account.id);
        resolved += res.resolved ?? 0;
        failed += res.failed ?? 0;
        remaining = res.remaining ?? 0;
        // Una tanda sin avances (todo falló) no se repite en bucle.
        if ((res.resolved ?? 0) === 0) break;
      }
      setPurgeSummary({ resolved, failed, remaining });
      setConfirmOpen(false);
    } catch (err) {
      setPurgeError(
        err?.status === 403
          ? t("Solo un supervisor o administrador puede cerrar conversaciones en bloque.")
          : apiErrorMessage(err, t("No se pudieron cerrar las conversaciones. Intenta de nuevo."))
      );
      if (resolved > 0) setPurgeSummary({ resolved, failed, remaining });
    } finally {
      setPurging(false);
      if (resolved > 0) onChanged?.();
    }
  };

  return (
    <div className="shrink-0 px-4 py-3 border-b border-border bg-status-warning-soft/40 text-sm">
      <div className="flex items-start gap-2">
        <Archive className="w-4 h-4 shrink-0 mt-0.5 text-status-warning" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-fg">
            {t("Número retirado {{numero}}").replace("{{numero}}", phone)}
          </p>
          <p className="mt-0.5 text-xs text-fg-muted">
            {t(
              "Sus conversaciones no están en las colas de trabajo. Si un cliente vuelve a escribir por un número activo, su conversación vuelve sola a la bandeja."
            )}
          </p>

          {purgeSummary && (
            <p className="mt-1.5 text-xs text-fg" role="status">
              {t("{{n}} conversaciones cerradas.").replace("{{n}}", String(purgeSummary.resolved))}
              {purgeSummary.failed > 0 &&
                ` ${t("{{n}} no se pudieron cerrar.").replace("{{n}}", String(purgeSummary.failed))}`}
              {purgeSummary.remaining > 0 &&
                ` ${t("Quedan {{n}} abiertas.").replace("{{n}}", String(purgeSummary.remaining))}`}
            </p>
          )}

          {canManage && (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setMigrateOpen(true)}>
                {t("Migrar a otro número")}
              </Button>
              {open > 0 && (
                <Button size="sm" variant="outline" onClick={() => setConfirmOpen(true)}>
                  {t("Cerrar {{n}} abiertas").replace("{{n}}", String(open))}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => !purging && setConfirmOpen(false)}
        size="sm"
        title={t("Cerrar conversaciones abiertas")}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)} disabled={purging}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" size="sm" onClick={handlePurge} loading={purging}>
              {t("Cerrar {{n}} abiertas").replace("{{n}}", String(open))}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">
          {t(
            "Se cerrarán {{n}} conversaciones del número {{numero}} como cierre general. No se borra nada: quedan en \"Resueltos\" y en la búsqueda, y no cuentan como atenciones en los reportes."
          )
            .replace("{{n}}", String(open))
            .replace("{{numero}}", phone)}
        </p>
        {purgeError && (
          <div role="alert" className="mt-3 flex items-start gap-2 text-sm text-status-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{purgeError}</span>
          </div>
        )}
      </Modal>

      <RetireNumberModal
        isOpen={migrateOpen}
        integrationId={account?.id}
        onClose={() => setMigrateOpen(false)}
        onRetired={() => onChanged?.()}
      />
    </div>
  );
}
